<template>
  <div class="container">
    <o-bread-crumb :url="{ name: 'VoucherGiftHistory' }">
      <template v-slot:pageName>Gift History</template>
      <template v-slot:pageDetail>Voucher Recipients</template>
    </o-bread-crumb>
    
    <!-- Feature Disabled State -->
    <div v-if="!isVoucherGiftEnabled" class="feature-disabled-container mt-4">
      <div class="disabled-content">
        <i class="fas fa-toggle-off fa-3x primary-text mb-3"></i>
        <h4>Voucher Gift Disabled</h4>
        <p>The Voucher Gift feature has been disabled by System Administration. Please contact System Owner to enable this feature.</p>
      </div>
    </div>

    <template v-if="isVoucherGiftEnabled">
      <div class="card text-center bg-white shadow-none">
      <div class="card-header">
        <ul class="nav nav-pills card-header-pills justify-content-between ms-2 me-4 mt-2 mb-2">
          <li class="nav-item">
            <div class="d-flex d-inline-block">
              <h4 class="header-title">
                  <i class="fas fa-users me-2 primary-icon"></i>Voucher Recipients
              </h4>
            </div>
            <p class="header-subtitle me-4 mt-1">
              List of members who received the voucher 
              <strong v-if="meta.voucher_name" class="text-primary" :title="meta.voucher_name">
                {{ meta.voucher_name.length > 25 ? meta.voucher_name.substring(0, 25) + '..' : meta.voucher_name }}
              </strong>
            </p>
          </li>
          <li class="nav-item d-flex align-items-center gap-2">
            <div class="search-input-wrapper">
              <input type="text" v-model="searchQuery" @keyup.enter="handleSearch" placeholder="Search member name..." />
              <button v-if="!isSearchActive" class="btn-search" @click="handleSearch">
                <i class="fas fa-search"></i>
              </button>
              <button v-else class="btn-search" @click="clearSearch">
                <i class="fas fa-times text-danger"></i>
              </button>
            </div>
            <button class="btn btn-refresh" @click.prevent="load_state()" title="Refresh">
              <i class="fas fa-sync-alt"></i>
            </button>
          </li>
        </ul>
      </div>
      <div class="card-body">
        <div class="spinner d-flex justify-content-center align-items-center" v-if="page.isLoading">
          <div class="spinner-border text-primary" role="status"></div>
        </div>
        <div v-if="page.output">
          <o-table style="text-align:left;" class="table-hover member-list-table mt-4">
            <thead>
              <tr v-if="fields">
                <th class="fw-bolder text-muted" scope="col" v-for="(field, index) in fields" :key="index"
                    :style="field === 'Member ID' || field === 'No' ? 'width: 1%; white-space: nowrap;' : ''">
                  {{ field }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in members" :key="index" class="member-row-clickable">
                <td>{{ (meta.page - 1) * meta.limit + index + 1 }}</td>
                <td>{{ item.member_id }}</td>
                <td>{{ item.first_name }} {{ item.last_name || '' }}</td>
                <td>{{ item.alias_id || '-' }}</td>
                <td>{{ item.email || '-' }}</td>
                <td>{{ item.tier_name || '-' }}</td>
              </tr>
              <tr v-if="members.length === 0">
                  <td colspan="6" class="text-center py-4 text-muted">No member found</td>
              </tr>
            </tbody>
          </o-table>
        </div>
      </div>
      <div class="container mt-5">
        <o-pagination :page="meta.page" :total="meta.last_page" @callback="navPage" />
      </div>
      </div>
    </template>
  </div>
</template>

<script>
import { OBreadCrumb, OTable, OPagination } from '@/components/OmnihControl';
import { onMounted, reactive, ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import store from '@/store/';
import { useToast } from '@/composables/useToast';

export default {
  setup() {
    const route = useRoute();
    const { showToast } = useToast();

    const isVoucherGiftEnabled = computed(() => {
        const flag = store.getters['FeatureFlag/getVoucherGiftFlag'];
        return flag !== 'NONACTIVE';
    });

    const page = reactive({
      isLoading: false,
      output: false
    });

    const fields = ref(['No', 'Member ID', 'Name', 'Member Alias', 'Email', 'Tier']);
    const members = ref([]);
    const meta = ref({
      page: 1,
      limit: 10,
      total: 0,
      last_page: 1
    });

    const searchQuery = ref('');
    const isSearchActive = ref(false);

    onMounted(() => {
      load_state();
    });

    function load_state() {
      page.isLoading = true;
      const payload = {
        id: route.params.id,
        meta: {
          page: meta.value.page,
          limit: meta.value.limit
        },
        search: {
          memberName: isSearchActive.value ? searchQuery.value : ''
        }
      };

      store.dispatch('VoucherGift/getHistoryMembers', payload)
        .then((response) => {
          members.value = response.data_res || [];
          meta.value = response.meta || meta.value;
        })
        .catch((err) => {
          console.error(err);
          showToast({ type: 'error', title: 'Error', message: 'Failed to load member list' });
        })
        .finally(() => {
          page.isLoading = false;
          page.output = true;
        });
    }

    function navPage(pageNumber) {
      meta.value.page = pageNumber;
      load_state();
    }

    function handleSearch() {
      meta.value.page = 1;
      isSearchActive.value = !!searchQuery.value;
      load_state();
    }

    function clearSearch() {
      searchQuery.value = '';
      isSearchActive.value = false;
      meta.value.page = 1;
      load_state();
    }

    return {
      page,
      fields,
      members,
      meta,
      searchQuery,
      isSearchActive,
      load_state,
      navPage,
      handleSearch,
      clearSearch,
      isVoucherGiftEnabled
    };
  },
  components: {
    OTable,
    OPagination,
    OBreadCrumb
  }
};
</script>

<style scoped>
/* Feature Disabled State */
.feature-disabled-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 2rem;
}

.disabled-content {
    text-align: center;
    color: #6b7280;
}

.disabled-content h4 {
    color: #374151;
    margin-bottom: 0.5rem;
    font-weight: 600;
}

.disabled-content p {
    margin: 0;
    font-size: 0.95rem;
}

.container {
  overflow-x: auto;
}

.table th,
.table td {
  word-wrap: break-word;
  white-space: normal;
}

.search-input-wrapper {
    display: flex;
    align-items: center;
    gap: 0;
    position: relative;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 0;
    transition: all 0.2s;
    overflow: hidden;
}

.search-input-wrapper:focus-within {
    border-color: var(--primary-color, #0d6efd);
    box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.1);
}

.search-input-wrapper input {
    border: none;
    padding: 10px 16px;
    font-size: 14px;
    width: 250px;
    outline: none;
    background: transparent;
    color: #334155;
    box-shadow: none;
}

.btn-search {
    background: transparent;
    border: none;
    width: 44px;
    height: 42px;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-search:hover {
    background: #f1f5f9;
    color: var(--primary-color, #0d6efd);
}

.btn-refresh {
    background-color: #fff;
    border: 1px solid #e2e8f0;
    color: #64748b;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    border-radius: 8px;
}

.btn-refresh:hover {
    background-color: #f1f5f9;
    color: #1e293b;
    border-color: #cbd5e1;
}

.member-list-table {
    white-space: nowrap;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
}

.member-list-table th,
.member-list-table td {
    padding: 12px 16px !important;
    vertical-align: middle;
}

.member-row-clickable {
    transition: background-color 0.2s;
}

.member-row-clickable:hover {
    background-color: #f8fafc !important;
}

.header-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.25rem 0;
    display: flex;
    align-items: center;
}

.header-subtitle {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
    text-align: left;
}
</style>
